/**
 * ShowcaseModeContext — drives the autonomous "showcase reel" played
 * from Settings → Developer → "Play showcase reel."
 *
 * Kept separate from DemoModeContext on purpose: that file's own
 * header is explicit that it manages exactly two axes (Preview
 * Studio's admin-granted canUseDemo/isDemo, and the developer-console
 * password gate). Whether the showcase reel is currently playing has
 * nothing to do with admin access or realtime revocation — it is a
 * plain session-local boolean a developer flips on and taps anywhere
 * to dismiss. Bolting a third, unrelated axis onto DemoModeContext
 * would blur the "two independent axes" contract that file documents.
 *
 * isShowcaseActive is read by <ShowcaseTour />, mounted once near the
 * router root in App.tsx beside <NovaLauncher />, to decide whether
 * to render anything at all — false for every studio, every load,
 * until this is flipped on.
 */
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ShowcaseModeContextType {
  /** True while the showcase reel (splash + autonomous tour) is playing. */
  isShowcaseActive: boolean;
  /** Starts the reel from the top: splash, then the tour loop. */
  startShowcase: () => void;
  /** Ends the reel immediately, wherever it currently is. */
  stopShowcase: () => void;
}

const ShowcaseModeContext = createContext<ShowcaseModeContextType | undefined>(undefined);

export function ShowcaseModeProvider({ children }: { children: ReactNode }) {
  const [isShowcaseActive, setIsShowcaseActive] = useState(false);

  const startShowcase = useCallback(() => setIsShowcaseActive(true), []);
  const stopShowcase = useCallback(() => setIsShowcaseActive(false), []);

  return (
    <ShowcaseModeContext.Provider value={{ isShowcaseActive, startShowcase, stopShowcase }}>
      {children}
    </ShowcaseModeContext.Provider>
  );
}

export function useShowcaseMode() {
  const context = useContext(ShowcaseModeContext);
  if (context === undefined) {
    // Safe fallback for the same reason DemoModeContext's useDemoMode
    // has one (Vite HMR re-evaluating this module before the provider
    // tree above it remounts) — a no-op default keeps the app
    // rendering instead of throwing on a stale reference.
    return {
      isShowcaseActive: false,
      startShowcase: () => {},
      stopShowcase: () => {},
    } satisfies ShowcaseModeContextType;
  }
  return context;
}
